// =============================================================================
// MeuDriver — Admin: Broadcast de Notificações (Batch V2)
// Rota: /admin/notificacoes
// =============================================================================

import { useState } from 'react'
import { Bell, Send, Search, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'
import {
  useSendNotification,
  useBroadcastLog,
  type NotificationTargetType,
  type AdminBroadcastLogRow,
} from '@/hooks/useAdminNotifications'
import type { ProfileRow } from '@/types/database'

// ── Schema de validação ───────────────────────────────────────────────────────

const schema = z.object({
  title: z.string().min(1, 'Título obrigatório').max(100, 'Máximo 100 caracteres'),
  body: z.string().min(1, 'Mensagem obrigatória').max(500, 'Máximo 500 caracteres'),
  target_type: z.enum(['all', 'students', 'instructors', 'user'] as const),
  target_email: z.string().optional(),
  action_url: z
    .string()
    .refine(
      (v) =>
        !v ||
        v.startsWith('/') ||
        /^https?:\/\/(localhost(:\d+)?|meudriver\.com\.br|www\.meudriver\.com\.br)/i.test(v),
      { message: 'Use caminho relativo (ex: /aluno/dashboard) ou domínio meudriver.com.br' },
    )
    .optional()
    .or(z.literal('')),
})

type FormValues = z.infer<typeof schema>

// ── Helpers ──────────────────────────────────────────────────────────────────

const TARGET_LABELS: Record<NotificationTargetType, string> = {
  all: 'Todos os usuários',
  students: 'Apenas alunos',
  instructors: 'Apenas instrutores',
  user: 'Usuário específico',
}

function targetBadge(type: NotificationTargetType) {
  const colors: Record<NotificationTargetType, string> = {
    all: 'bg-purple-100 text-purple-800',
    students: 'bg-blue-100 text-blue-800',
    instructors: 'bg-teal-100 text-teal-800',
    user: 'bg-orange-100 text-orange-800',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[type]}`}>
      {TARGET_LABELS[type]}
    </span>
  )
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function AdminNotifications() {
  const sendMutation = useSendNotification()
  const { data: log, isLoading: logLoading } = useBroadcastLog()

  // Busca de usuário específico
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [userSearchLoading, setUserSearchLoading] = useState(false)
  const [foundProfile, setFoundProfile] = useState<ProfileRow | null>(null)
  const [userSearchError, setUserSearchError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { target_type: 'all' },
  })

  const targetType = watch('target_type')

  // ── Busca de usuário por e-mail ─────────────────────────────────────────────

  async function handleSearchUser() {
    const term = userSearchTerm.trim()
    if (!term) return
    setUserSearchLoading(true)
    setFoundProfile(null)
    setUserSearchError(null)
    try {
      // profiles não armazena email — busca via admin_users_view (se existir)
      // ou pela RPC get_admin_users. Como fallback, busca por phone ou full_name.
      // Aqui buscamos por full_name contendo o termo; se for um UUID válido, busca por id.
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(term)

      const query = supabase.from('profiles').select('id, full_name, avatar_url, role, phone')

      const { data, error } = isUuid
        ? await query.eq('id', term).maybeSingle()
        : await query.ilike('full_name', `%${term}%`).limit(1).maybeSingle()

      if (error) throw error
      if (!data) {
        setUserSearchError('Nenhum usuário encontrado. Busque por UUID ou parte do nome.')
        return
      }
      setFoundProfile(data as unknown as ProfileRow)
    } catch {
      setUserSearchError('Erro ao buscar usuário.')
    } finally {
      setUserSearchLoading(false)
    }
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  async function onSubmit(values: FormValues) {
    try {
      if (values.target_type === 'user' && !foundProfile) {
        toast.error('Selecione um usuário específico primeiro.')
        return
      }

      await sendMutation.mutateAsync({
        target_type: values.target_type,
        target_user_id: values.target_type === 'user' ? (foundProfile?.id ?? null) : null,
        title: values.title,
        body: values.body,
        action_url: values.action_url || null,
      })

      toast.success('Notificação enviada!')
      reset({ target_type: 'all' })
      setFoundProfile(null)
      setUserSearchTerm('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar notificação.')
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const todayEntries = (log ?? []).filter((entry) => {
    const d = new Date(entry.sent_at)
    const now = new Date()
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    )
  })

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Bell className="h-6 w-6 text-brand-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Broadcast de Notificações</h1>
          <p className="text-sm text-muted-foreground">Envie notificações in-app para grupos de usuários.</p>
        </div>
      </div>

      {/* Contadores */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-2xl font-bold text-gray-900">{todayEntries.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Enviadas hoje</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-2xl font-bold text-gray-900">{log?.length ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Total (últimas 20)</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Formulário */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nova notificação</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Destinatários */}
              <div className="space-y-1.5">
                <Label>Destinatários</Label>
                <Select
                  value={targetType}
                  onValueChange={(v) => {
                    setValue('target_type', v as NotificationTargetType)
                    setFoundProfile(null)
                    setUserSearchTerm('')
                    setUserSearchError(null)
                  }}
                >
                  <SelectTrigger data-testid="notif-target-select">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os usuários</SelectItem>
                    <SelectItem value="students">Apenas alunos</SelectItem>
                    <SelectItem value="instructors">Apenas instrutores</SelectItem>
                    <SelectItem value="user">Usuário específico</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Busca de usuário específico */}
              {targetType === 'user' && (
                <div className="space-y-2 rounded-xl border border-dashed border-gray-200 p-3">
                  <Label>Buscar usuário por nome ou UUID</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nome do usuário ou UUID"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchUser() } }}
                      data-testid="notif-user-search-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleSearchUser}
                      disabled={userSearchLoading}
                      data-testid="notif-user-search-btn"
                    >
                      {userSearchLoading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Search className="h-4 w-4" />
                      }
                    </Button>
                  </div>
                  {userSearchError && (
                    <p className="text-xs text-destructive">{userSearchError}</p>
                  )}
                  {foundProfile && (
                    <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                      <span className="text-sm font-medium text-green-800">
                        {foundProfile.full_name ?? 'Sem nome'}
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {foundProfile.role}
                      </Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Título */}
              <div className="space-y-1.5">
                <Label htmlFor="notif-title">
                  Título <span className="text-xs text-muted-foreground">(máx. 100)</span>
                </Label>
                <Input
                  id="notif-title"
                  {...register('title')}
                  maxLength={100}
                  placeholder="Ex: Nova funcionalidade disponível"
                  data-testid="notif-title-input"
                />
                {errors.title && (
                  <p className="text-xs text-destructive">{errors.title.message}</p>
                )}
              </div>

              {/* Corpo */}
              <div className="space-y-1.5">
                <Label htmlFor="notif-body">
                  Mensagem <span className="text-xs text-muted-foreground">(máx. 500)</span>
                </Label>
                <Textarea
                  id="notif-body"
                  {...register('body')}
                  maxLength={500}
                  rows={4}
                  placeholder="Descreva o conteúdo da notificação..."
                  data-testid="notif-body-input"
                />
                {errors.body && (
                  <p className="text-xs text-destructive">{errors.body.message}</p>
                )}
              </div>

              {/* URL de ação (opcional) */}
              <div className="space-y-1.5">
                <Label htmlFor="notif-action-url">
                  URL de ação <span className="text-xs text-muted-foreground">(opcional)</span>
                </Label>
                <Input
                  id="notif-action-url"
                  {...register('action_url')}
                  placeholder="https://... ou /aluno/dashboard"
                  data-testid="notif-action-url-input"
                />
                {errors.action_url && (
                  <p className="text-xs text-destructive">{errors.action_url.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || sendMutation.isPending}
                data-testid="notif-send-btn"
              >
                {isSubmitting || sendMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Enviar notificação
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Histórico */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Histórico de broadcasts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {logLoading ? (
              <div className="space-y-3 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !log || log.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                Nenhum broadcast enviado ainda.
              </p>
            ) : (
              <div className="divide-y">
                {log.map((entry: AdminBroadcastLogRow) => (
                  <div key={entry.id} className="px-5 py-3 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 leading-snug flex-1">
                        {entry.title}
                      </p>
                      {targetBadge(entry.target_type)}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{entry.body}</p>
                    <p className="text-[10px] text-gray-400">{formatDate(entry.sent_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
